import { Component, DestroyRef, ViewChild } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { AppConfigService } from '@markant-platform/shared/data-access';
import { AuthenticatedUserService, UserService } from '@markant-platform/shared/user/data-access';
import { DialogService } from '@markant-platform/shared/dialog/feature';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { catchError, combineLatestWith, EMPTY, map, Observable, of, tap } from 'rxjs';
import { MarkantPopover } from '@markant/elements-angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { removeStorageKeysStartingWith } from '@markant-platform/shared/util-helpers';
import { UpgradeSuggestedStoragePrefix } from '@markant-platform/cartridge/model';
import { OrganizationMembership } from 'data-model';

@Component({
  selector: 'markant-platform-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
})
export class UserMenuComponent {
  @ViewChild(MarkantPopover, { static: true }) popover!: MarkantPopover;

  protected readonly platformVersion = AppConfigService.appConfiguration.platformVersion;
  showApproveOrganizationChangeDialog = false;
  currentActiveOrganizationId?: string;
  organizationIdToSetActive?: string;
  showOrganizationChangeForSingleOrganizationMembership = false;
  loading = true;
  organizationMemberships$: Observable<OrganizationMembership[]>;
  showPopover = false;
  listItems: { label: string; routeLink?: string; clickFunction?(): void }[] = [
    {
      label: 'settings',
      routeLink: '/user-administration',
    },
    {
      label: 'logout',
      clickFunction: (): Observable<void> => {
        removeStorageKeysStartingWith(UpgradeSuggestedStoragePrefix);
        return this.authService.logout({
          logoutParams: {
            returnTo: AppConfigService.appConfiguration.logoutPageUrl,
          },
        });
      },
    },
  ];

  constructor(
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly dialogService: DialogService,
    private readonly snackbarService: SnackbarService,
    private readonly destroyRef: DestroyRef
  ) {
    this.organizationMemberships$ = this.authenticatedUserService.getUser().pipe(
      combineLatestWith(this.authenticatedUserService.getOrganizationId()),
      map(([user, organizationId]) => {
        return {
          organizationId: organizationId,
          organizationMemberships:
            user.organizationMemberships?.sort((a, b) => {
              if (a.organizationId === organizationId) {
                return -1;
              }
              if (b.organizationId === organizationId) {
                return 1;
              }
              return a.organizationName.localeCompare(b.organizationName);
            }) ?? [],
        };
      }),
      tap(({ organizationId, organizationMemberships }) => {
        if (organizationId.trim().length !== 0) {
          this.currentActiveOrganizationId = organizationId;
          this.showOrganizationChangeForSingleOrganizationMembership = !organizationMemberships.some(
            organizationMembership =>
              organizationMembership.organizationId === organizationId &&
              (organizationMembership.userOrganizationStatus === 'ACTIVE' ||
                organizationMembership.userOrganizationStatus === 'INVITED')
          );
        } else {
          this.showOrganizationChangeForSingleOrganizationMembership = true;
        }
      }),
      map(data => data.organizationMemberships),
      map(organizationMemberships =>
        organizationMemberships.filter(
          organizationMembership =>
            organizationMembership.userOrganizationStatus === 'ACTIVE' ||
            organizationMembership.userOrganizationStatus === 'INVITED'
        )
      ),
      catchError(() => {
        return of([]);
      })
    );
  }

  clickItem(item: { clickFunction?(): void }): void {
    if (item.clickFunction !== undefined) {
      item.clickFunction();
    }
    this.closePopover();
  }

  togglePopover(): void {
    this.showPopover = !this.showPopover;
  }

  closePopover(): void {
    this.showPopover = false;
  }

  changeOrganization(organizationId?: string): void {
    if (this.currentActiveOrganizationId === organizationId) {
      return;
    }
    this.organizationIdToSetActive = organizationId;
    this.dialogService.showApproveDialogWithTransloco(
      'userMenu.approve.headline',
      'userMenu.approve.text',
      () => {
        this.approveChangeOrganization();
      },
      'userMenu.approve.button'
    );
    this.closePopover();
  }

  approveChangeOrganization(): void {
    if (this.organizationIdToSetActive && this.authenticatedUserService.userId) {
      this.userService
        .changeActiveOrganization(this.authenticatedUserService.userId, this.organizationIdToSetActive)
        .pipe(
          catchError(() => {
            this.snackbarService.showErrorSnackbarWithTransloco('userMenu.changeError');
            this.organizationIdToSetActive = undefined;
            return EMPTY;
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => {
          this.authService.logout({
            logoutParams: {
              returnTo: AppConfigService.appConfiguration.logoutPageUrl,
            },
          });
          removeStorageKeysStartingWith(UpgradeSuggestedStoragePrefix);
        });
    }
  }
}
